<script setup lang="ts">
import { computed, ref } from 'vue'
import BButton from '../bButton.vue'
import bDropdown from '@/components/common/bDropdown.vue'
import Chat from '@/components/common/chat.vue'
import TextAreaInput from '@/components/common/textAreaInput.vue'
import PhUserCirclePlus from '~icons/ph/user-circle-plus'
import PhArrowFatLineRightBold from '~icons/ph/arrow-fat-line-right-bold'
import PhPencilSimpleLine from '~icons/ph/pencil-simple-line'
import PhTrash from '~icons/ph/trash'
import PhDotsThreeVertical from '~icons/ph/dots-three-vertical'
import { useUserStore } from '@/config/store/userStore'
import PhInfo from '~icons/ph/info'
import MentionArea from '@/components/common/MentionArea/MentionArea.vue'
import BConfirm from '@/components/common/bConfirm.vue'
import CommentsEmptyState from './CommentsEmptyState.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
withDefaults(defineProps<{
  width?: string
  commentContainerClass?: string
  tooltip?: string
}>(), {
  width: 'w-[400px] max-w-[1000px]',
  commentContainerClass: 'max-h-[50dvh]',
})

const emits = defineEmits<{
  'addComment': [comment?: TComment ]
  'deleteComment': [commentId: string]
  'updateComment': [commentId: string, content: string],
}>()

const userStore = useUserStore()

const editingId = ref<string | null>(null)
const editingContent = ref<string>('')
const editingMentions  = ref<TComment['taggedPopulated']>([])

type TComment = {
  _id?: string
  tempId?: string
  _created: Date
  message: string
  tagged: string[]
  taggedPopulated: {
    _id: string
    firstname: string
    lastname: string
    picture: string
  }[]
  authorId: string
  author: {
    _id: string
    firstname: string
    lastname: string
    picture: string
  }
}

const comments = defineModel<TComment[]>()
const userInput = ref('')
const userMentions = ref([])


const addComment = () => {
  if (userInput.value.trim() === '')
    return

  const tempId = Math.random().toString(36).substring(7)


  emits('addComment', {
    tempId,
    _created: new Date(),
    message: userInput.value,
    tagged: userMentions.value.map(mention => mention._id),
    author: {
      _id: userStore.user._id,
      firstname: userStore.user.firstname,
      lastname: userStore.user.lastname,
      picture: userStore.user.picture,
    },
    taggedPopulated: userMentions.value,
    authorId: userStore.user._id,
  })
  userInput.value = ''
}

const commentActions = (commentId: string) => [
  {
    _id: `edit-${commentId}`,
    display: t('components.common.comment.edit'),
    click: () => {
      const c = comments.value.find(c => (c._id || c.tempId) === commentId)!
      startEdit(c)
    },
  },
  {
    _id: `delete-${commentId}`,
    display: t('components.common.comment.delete'),
    bg: 'error',
    click: () => onDeleteConfirm(commentId),
  },
]

const confirmCallback = () => {
  if (confirmModal.value.toDeleteId) {
    emits('deleteComment', confirmModal.value.toDeleteId)
  }
  confirmModal.value.isOpen = false
  confirmModal.value.toDeleteId = null
}

const cancelCallback = () => {
  confirmModal.value.isOpen = false
  confirmModal.value.toDeleteId = null
}

const confirmModal = ref({
  isOpen: false as boolean,
  message: t('components.common.comment.confirmDelete'),
  toDeleteId: '' as string | null,
  confirmCallback,
  cancelCallback
})


const startEdit = (c: TComment) => {
  editingId.value = c._id || c.tempId || null
  editingContent.value = c.message
  // Comments loaded with the process or just added come without their mentions
  editingMentions.value = [...(c.taggedPopulated || [])]
}

const cancelEdit = () => {
  editingId.value = null
  editingContent.value = ''
  editingMentions.value = []
}

const saveEdit = () => {
  if (!editingId.value) return;
  const newText = editingContent.value.trim();
  const idx = comments.value.findIndex(c => (c._id || c.tempId) === editingId.value);
  if (idx !== -1) {
    comments.value[idx].message = newText
    comments.value[idx].taggedPopulated = [...editingMentions.value]
    comments.value[idx].tagged = editingMentions.value.map(m => m._id)
  }
  emits('updateComment', editingId.value, newText);
  cancelEdit();
}

function onDeleteConfirm(id?: string) {
  if (!id) return
  confirmModal.value.toDeleteId = id
  confirmModal.value.isOpen = true
}


const canSendMessage = computed(() => userInput.value.trim() !== '')
</script>

<template>
  <div class="flex flex-col gap-2 grow" :class="width">
    <BConfirm
      v-model:is-open="confirmModal.isOpen"
      :message="confirmModal.message"
      :confirm-callback="confirmModal.confirmCallback"
      :cancel-callback="confirmModal.cancelCallback"
    />

    <div class="text-xl subtitle flex gap-2 items-center">
      <slot name="header">
        {{ t('components.common.comment.title') }} <span v-if="tooltip" v-tooltip="tooltip"><PhInfo class="cursor-help" /></span>
      </slot>
    </div>
    <div class="p-2 rounded-xl bg-fade shadow-inner grow flex flex-col gap-4">
      <div class="flex gap-2 items-end">
        <MentionArea v-model:mentions="userMentions" v-model="userInput" class="max-h-96 grow overflow-auto" />   
        <div class="flex flex-col items-end gap-1">
          <!-- <BButton v-tooltip="'Not yet available'" icon bg="base" disabled>
            <PhUserCirclePlus />
          </BButton> -->
          <BButton :disabled="!canSendMessage" size="large" data-cy="comment-send" @click="addComment">
            <PhArrowFatLineRightBold />
          </BButton>
        </div>
      </div>
      <ul v-if="comments.length > 0" v-auto-animate class="overflow-auto" :class="[commentContainerClass]">
        <li
          v-for="comment in comments"
          :key="comment._id || comment.tempId"
          class="relative flex flex-col gap-2 my-8"
        >
          <div v-if="editingId === (comment._id || comment.tempId)" class="flex flex-col gap-2">
            <MentionArea
              v-model="editingContent"
              v-model:mentions="editingMentions"
              class="w-full"
              :tooltip="t('components.common.comment.tooltip', { signe: '@' })"
            />
            <div class="flex gap-2">
              <BButton size="small" @click="saveEdit">{{ t('components.common.comment.save') }}</BButton>
              <BButton size="small" variant="outline" @click="cancelEdit">{{ t('components.common.comment.cancel') }}</BButton>
            </div>
          </div>

          <Chat
            :chat="comment"
            class="flex-1"
            @edit="startEdit(comment)"
            @delete="onDeleteConfirm(comment._id||comment.tempId)"
          >
            <template #actions>
              <bDropdown
                v-if="comment.authorId === userStore.user._id"
                :items="commentActions(comment._id || comment.tempId)"
                display=""
                data-cy="dropdown-comment-options"
              >
                <template #triggerContent>
                  <PhDotsThreeVertical class="cursor-pointer"/>
                </template>
              </bDropdown>
            </template>
          </Chat>
        </li>
      </ul>
      <CommentsEmptyState v-else />
    </div>
  </div>
</template>
